// =============================================================================
// supplementInvoicePDF.ts
//
// Generates the final supplement invoice PDF the homeowner sees attached to
// Shannon's email + at /i/{token}. Mirrors the installAgreementPDF.ts pattern
// (pdf-lib in-browser, WinAnsi-safe characters, auto-pagination).
//
// Variant 2 - Professional Classic design (approved June 2026):
//   White header with duck logo + company address, teal accent line,
//   bordered BILL TO / DETAILS boxes, teal table header, dark slate
//   summary box with peach balance due, teal pay-online section,
//   check payee section, slate footer bar.
//
// Usage:
//   import { generateSupplementInvoicePDF, uploadInvoicePDF } from "./lib/supplementInvoicePDF";
//   const pdfBytes = await generateSupplementInvoicePDF(deal, paymentsSoFar);
//   const storagePath = await uploadInvoicePDF(supabase, deal.id, pdfBytes);
//   // then UPDATE deals SET supplement_invoice_pdf_url = storagePath
//
// Auto-paginates: typical output is 1-2 pages.
// =============================================================================

import { PDFDocument, PDFFont, PDFImage, PDFPage, rgb, StandardFonts } from "pdf-lib";
import type { SupabaseClient } from "@supabase/supabase-js";

// --- types ------------------------------------------------------------------

export type InvoiceDeal = {
  id: string;
  homeowner_name: string | null;
  homeowner_email: string | null;
  homeowner_phone: string | null;
  property_street: string | null;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  insurance_company: string | null;
  claim_number?: string | null;

  final_invoice_amount: number | string | null;
  /** Gross work value (carrier-facing). Preferred total for the printed
   *  invoice — the PDF must always be carrier-safe (Jul 14, 2026: net on
   *  screen, gross on paper). Falls back to final_invoice_amount when absent
   *  (legacy deals where only the manual final exists). */
  initial_invoice_amount?: number | string | null;
  supplement_invoice_amount: number | string | null;
  supplement_approved_amount: number | string | null;
  post_build_balance_remaining: number | string | null;

  /** Oct 29, 2026 — trades billed to the homeowner from the trade sheet (SQL
   *  trade_sync_deal). Already inside the gross; printed as one indented
   *  "Includes <label>" line each when trade_ho_total > 0. trade_ho_lines is
   *  [{ trade_id, label, amount }] — customer price only, never a sub figure. */
  trade_ho_total?: number | string | null;
  trade_ho_lines?: unknown;

  pre_build_amount_received?: number | string | null;
  post_build_amount_received?: number | string | null;

  /** Homeowner payments submitted but not yet settled — Stripe ACH in
   *  deal_payments with state = 'made'. Each gets its own labeled credit line
   *  and is netted out of BALANCE DUE. Build with buildPendingPayments(). */
  pending_payments?: { label: string; amount: number }[];

  build_completed_date?: string | null;

  // Set by generateAndAttachInvoicePDF after upload; read by SendInvoicePanel
  // for the "Generated X ago" label on the invoice-attached card.
  supplement_invoice_pdf_url?: string | null;
  supplement_invoice_generated_at?: string | null;

  // Snapshot of the figures the PDF was generated with — used by SendInvoicePanel
  // to detect a stale PDF (amount corrected or a payment posted after generation).
  supplement_invoice_pdf_amount?: number | string | null;
  supplement_invoice_pdf_received?: number | string | null;
  supplement_invoice_pdf_pending?: number | string | null;
};

// --- invoice number helper --------------------------------------------------
//
// Insurance (claim_number present): {cleanClaim}-MMYY
//   e.g. claim "12345-A" generated May 2026 -> "12345A-0526"
//
// Retail (no claim_number): {house_number}-MMDDYY
//   e.g. address "4029 Willows Way" generated May 13, 2026 -> "4029-051326"
//
// Pure function. Same deal + same generatedAt always produces same number.
// Stored on deals.supplement_invoice_number so re-sends + check memo + the
// /i/{token} page all show the same value.

export function computeInvoiceNumber(deal: Pick<InvoiceDeal, "claim_number" | "property_street">, generatedAt?: Date): string {
  const d = generatedAt ?? new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);

  const claim = deal.claim_number?.trim();
  if (claim) {
    const cleanClaim = claim.replace(/[^a-zA-Z0-9]/g, "");
    return cleanClaim || "CLAIM" + "-" + mm + yy;
  }

  const street = deal.property_street?.trim() ?? "";
  const houseMatch = street.match(/^(\d+)/);
  const houseNum = houseMatch ? houseMatch[1] : "RETAIL";
  return houseNum + "-" + mm + dd + yy;
}

// --- helpers ----------------------------------------------------------------

// Oct 29, 2026 — the customer lines of the trades billed to the homeowner, read
// defensively from deals.trade_ho_lines. Nothing unless trade_ho_total > 0; a
// total with no readable lines prints as one "other trades" line.
function invoiceTradeLines(deal: InvoiceDeal): { label: string; amount: number }[] {
  const total = Number(deal.trade_ho_total ?? 0);
  if (!(total > 0)) return [];
  const raw = Array.isArray(deal.trade_ho_lines) ? (deal.trade_ho_lines as unknown[]) : [];
  const lines = raw
    .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
    .map((l) => ({
      label: pdfSafeLabel(String(l.label ?? "")) || "trade work",
      amount: Math.round((Number(l.amount) || 0) * 100) / 100,
    }))
    .filter((l) => l.amount !== 0);
  return lines.length > 0 ? lines : [{ label: "other trades", amount: Math.round(total * 100) / 100 }];
}

// A trade line's label can carry text typed on the trade sheet (an off-list
// line, an "other" job), so it may hold characters the PDF's standard font
// cannot draw (pdf-lib throws "WinAnsi cannot encode"): the common ones map to
// plain text, the rest become "?" (the rule of src/lib/winAnsiSafe.ts, which
// the server copy cannot import). Cut to fit beside the amount column.
const PDF_LABEL_MAP: Record<string, string> = {
  "\u2013": "-", "\u2014": "-", "\u2212": "-", "\u2018": "'", "\u2019": "'", "\u2032": "'",
  "\u201C": '"', "\u201D": '"', "\u2033": '"', "\u00D7": "x", "\u2026": "...", "\u2022": "*",
};
function pdfSafeLabel(s: string): string {
  let out = "";
  for (const ch of s.replace(/\s+/g, " ").trim()) {
    const code = ch.codePointAt(0) ?? 0x3f;
    if (PDF_LABEL_MAP[ch] !== undefined) out += PDF_LABEL_MAP[ch];
    else if ((code >= 0x20 && code < 0x7f) || (code >= 0xa0 && code <= 0xff)) out += ch;
    else out += "?";
  }
  return out.length > 72 ? `${out.slice(0, 69).trimEnd()}...` : out;
}

// Brand palette - Variant 2 Professional Classic
const C = {
  orange:   rgb(0.957, 0.396, 0.188),   // #f46530
  slate:    rgb(0.165, 0.18,  0.188),    // #2a2e30
  peach:    rgb(1,     0.62,  0.478),    // #ff9e7a
  teal:     rgb(0.204, 0.361, 0.447),    // #345c72
  sky:      rgb(0.831, 0.929, 0.957),    // #d4edf4
  line:     rgb(0.875, 0.902, 0.918),    // #dfe6ea
  slate60:  rgb(0.337, 0.361, 0.376),    // #565c60
  slate40:  rgb(0.541, 0.565, 0.58),     // #8a9094
  white:    rgb(1, 1, 1),
  red:      rgb(0.82,  0.22,  0.22),     // credit/negative amounts
};

const PAGE_W = 612;     // 8.5"
const PAGE_H = 792;     // 11"
const ML = 50;           // left margin
const MR = 50;           // right margin
const CONTENT_W = PAGE_W - ML - MR;
const BOTTOM_MARGIN = 60;
const TOP_MARGIN = 50;

type DrawCtx = {
  pdf: PDFDocument;
  page: PDFPage;
  bold: PDFFont;
  reg: PDFFont;
  logo: PDFImage | null;
  y: number;
};

function money(v: number | string | null | undefined): string {
  const n = Number(v ?? 0);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function asciiOnly(s: string): string {
  return s.replace(/−/g, "-").replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
}

function ensureSpace(ctx: DrawCtx, needed: number) {
  if (ctx.y - needed < BOTTOM_MARGIN) {
    ctx.page = ctx.pdf.addPage([PAGE_W, PAGE_H]);
    ctx.y = PAGE_H - TOP_MARGIN;
    drawContinuationHeader(ctx);
  }
}

// Right-aligned text helper
function drawRight(ctx: DrawCtx, text: string, opts: { y?: number; size?: number; bold?: boolean; color?: ReturnType<typeof rgb>; rightX?: number } = {}) {
  const size = opts.size ?? 10;
  const font = opts.bold ? ctx.bold : ctx.reg;
  const color = opts.color ?? C.slate;
  const w = font.widthOfTextAtSize(asciiOnly(text), size);
  const rx = opts.rightX ?? (PAGE_W - MR);
  ctx.page.drawText(asciiOnly(text), { x: rx - w, y: opts.y ?? ctx.y, size, font, color });
}

// --- header & footer --------------------------------------------------------

function drawPageHeader(ctx: DrawCtx) {
  const headerY = PAGE_H - TOP_MARGIN;
  ctx.y = headerY;

  // Logo on the left (duck2.png)
  if (ctx.logo) {
    const logoDims = ctx.logo.scale(1);
    const logoH = 48;
    const logoW = logoH * (logoDims.width / logoDims.height);
    ctx.page.drawImage(ctx.logo, { x: ML, y: headerY - logoH + 10, width: logoW, height: logoH });
  } else {
    // Fallback text wordmark if logo fails to load
    ctx.page.drawText("BARDIN ROOFING", {
      x: ML, y: headerY - 6, size: 18, font: ctx.bold, color: C.teal,
    });
  }

  // Company info on the right
  const rSize = 8.5;
  const rColor = C.slate60;
  const rX = PAGE_W - MR;
  drawRight(ctx, "Bardin Roofing", { y: headerY, size: 9, bold: true, color: C.slate, rightX: rX });
  drawRight(ctx, "225 Reformation Pkwy, Suite 200", { y: headerY - 13, size: rSize, color: rColor, rightX: rX });
  drawRight(ctx, "Canton, GA 30114", { y: headerY - 25, size: rSize, color: rColor, rightX: rX });
  drawRight(ctx, "(470) 823-8816", { y: headerY - 37, size: rSize, color: rColor, rightX: rX });

  // Teal accent line under header
  const lineY = headerY - 52;
  ctx.page.drawRectangle({
    x: ML, y: lineY, width: CONTENT_W, height: 2.5, color: C.teal,
  });

  ctx.y = lineY - 18;
}

function drawContinuationHeader(ctx: DrawCtx) {
  drawPageHeader(ctx);
  ctx.page.drawText(asciiOnly("(continued)"), {
    x: ML, y: ctx.y, size: 8.5, font: ctx.reg, color: C.slate40,
  });
  ctx.y -= 16;
}

function drawPageFooter(ctx: DrawCtx) {
  // Dark slate footer bar
  const barH = 32;
  const barY = 0;
  ctx.page.drawRectangle({ x: 0, y: barY, width: PAGE_W, height: barH, color: C.slate });
  ctx.page.drawText(
    asciiOnly("Peach Duck Contracting Co. d/b/a Bardin Roofing  |  225 Reformation Pkwy, Suite 200, Canton, GA 30114  |  (470) 823-8816"),
    { x: ML, y: barY + 11, size: 7, font: ctx.reg, color: C.slate40 },
  );
}

// --- main -------------------------------------------------------------------

export async function generateSupplementInvoicePDF(deal: InvoiceDeal, invoiceNumber?: string): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle("Bardin Roofing Invoice");
  pdf.setProducer("Bardin CRM");
  pdf.setCreationDate(new Date());

  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const reg  = await pdf.embedFont(StandardFonts.Helvetica);

  // Embed duck logo (best-effort)
  let logo: PDFImage | null = null;
  try {
    const logoBytes = await (await fetch("/img/duck2.png")).arrayBuffer();
    logo = await pdf.embedPng(logoBytes);
  } catch { /* continue without logo */ }

  const ctx: DrawCtx = {
    pdf,
    page: pdf.addPage([PAGE_W, PAGE_H]),
    bold,
    reg,
    logo,
    y: PAGE_H - TOP_MARGIN,
  };

  // === PAGE HEADER ===
  drawPageHeader(ctx);

  // === INVOICE BADGE (teal, top-right below header) ===
  const badgeText = "INVOICE";
  const badgeW = bold.widthOfTextAtSize(badgeText, 12) + 20;
  const badgeH = 24;
  const badgeX = PAGE_W - MR - badgeW;
  ctx.page.drawRectangle({ x: badgeX, y: ctx.y - 4, width: badgeW, height: badgeH, color: C.teal });
  ctx.page.drawText(badgeText, {
    x: badgeX + 10, y: ctx.y + 2, size: 12, font: bold, color: C.white,
  });
  ctx.y -= 36;

  // === BILL TO + DETAILS boxes ===
  const generatedAt = new Date();
  const today = generatedAt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const invNum = invoiceNumber ?? computeInvoiceNumber(deal, generatedAt);

  const boxGap = 16;
  const boxW = (CONTENT_W - boxGap) / 2;
  const boxH = 110;
  const boxY = ctx.y - boxH;
  const box1X = ML;
  const box2X = ML + boxW + boxGap;

  // Box outlines
  for (const bx of [box1X, box2X]) {
    ctx.page.drawRectangle({ x: bx, y: boxY, width: boxW, height: boxH, borderColor: C.line, borderWidth: 1, color: C.white });
  }

  // BILL TO box content
  const labelSize = 8;
  const valSize = 10;
  let by = ctx.y - 16;
  ctx.page.drawText("BILL TO", { x: box1X + 12, y: by, size: labelSize, font: bold, color: C.teal });
  by -= 16;
  ctx.page.drawText(asciiOnly(deal.homeowner_name ?? "Homeowner"), { x: box1X + 12, y: by, size: 11, font: bold, color: C.slate });
  by -= 14;
  if (deal.property_street) {
    ctx.page.drawText(asciiOnly(deal.property_street), { x: box1X + 12, y: by, size: valSize, font: reg, color: C.slate60 });
    by -= 13;
  }
  const cityLine = [deal.property_city, deal.property_state].filter(Boolean).join(", ") + (deal.property_zip ? " " + deal.property_zip : "");
  if (cityLine.trim()) {
    ctx.page.drawText(asciiOnly(cityLine), { x: box1X + 12, y: by, size: valSize, font: reg, color: C.slate60 });
    by -= 13;
  }
  if (deal.homeowner_email) {
    ctx.page.drawText(asciiOnly(deal.homeowner_email), { x: box1X + 12, y: by, size: 9, font: reg, color: C.slate40 });
  }

  // DETAILS box content
  by = ctx.y - 16;
  ctx.page.drawText("DETAILS", { x: box2X + 12, y: by, size: labelSize, font: bold, color: C.teal });
  by -= 16;
  // Invoice number
  ctx.page.drawText("Invoice #", { x: box2X + 12, y: by, size: 9, font: reg, color: C.slate40 });
  ctx.page.drawText(asciiOnly(invNum), { x: box2X + 80, y: by, size: valSize, font: bold, color: C.slate });
  by -= 14;
  // Date
  ctx.page.drawText("Date", { x: box2X + 12, y: by, size: 9, font: reg, color: C.slate40 });
  ctx.page.drawText(asciiOnly(today), { x: box2X + 80, y: by, size: valSize, font: reg, color: C.slate60 });
  by -= 14;
  // Claim number (if insurance)
  if (deal.claim_number) {
    ctx.page.drawText("Claim #", { x: box2X + 12, y: by, size: 9, font: reg, color: C.slate40 });
    ctx.page.drawText(asciiOnly(deal.claim_number), { x: box2X + 80, y: by, size: valSize, font: reg, color: C.slate60 });
    by -= 14;
  }
  if (deal.insurance_company) {
    ctx.page.drawText("Carrier", { x: box2X + 12, y: by, size: 9, font: reg, color: C.slate40 });
    ctx.page.drawText(asciiOnly(deal.insurance_company), { x: box2X + 80, y: by, size: valSize, font: reg, color: C.slate60 });
  }

  ctx.y = boxY - 20;

  // === LINE ITEMS TABLE ===
  // Jul 14, 2026 (net on screen, gross on paper): the printed invoice is
  // carrier-safe — Total Invoiced is the GROSS work value
  // (initial_invoice_amount), identical to what the carrier is billed, never
  // the netted collectible. The homeowner's actual (net) balance lives online
  // behind the payment link. Balance here = gross − payments received, i.e.
  // "remaining on this invoice" — when received amounts aren't supplied
  // (the /i token fallback render), fall back to the stored balance rather
  // than printing the full gross as due.
  const grossInv      = Number(deal.initial_invoice_amount ?? deal.final_invoice_amount ?? 0) ||
                        Number(deal.final_invoice_amount ?? 0);
  const supps         = Number(deal.supplement_approved_amount ?? 0);
  const hasReceived   = deal.pre_build_amount_received != null || deal.post_build_amount_received != null;
  const preRecv       = Number(deal.pre_build_amount_received ?? 0);
  const postRecv      = Number(deal.post_build_amount_received ?? 0);
  const totalRecv     = preRecv + postRecv;
  // Aug 14, 2026 — pending (in-flight) payments. ACH submitted through the
  // homeowner portal takes 1-4 business days to settle. Until now the printed
  // invoice ignored these entirely, so a homeowner who had already paid still
  // saw the full pre-payment balance (Keely, invoice 015171074803: printed
  // $5,065.11 when she actually owed $689.10). Show them as explicit pending
  // credits and net them out of BALANCE DUE, with a footnote that a returned
  // transfer goes back on the balance.
  // NOTE: deliberately NOT falling back to deals.*_amount_in_flight. That
  // rollup also counts insurance checks sitting at IINK for endorsement
  // (deal_payments.state = 'iink_submitted'), which are NOT homeowner
  // payments — crediting them here would under-bill. The caller must pass
  // pending_payments explicitly; see buildPendingPayments() below.
  const pendingItems  = deal.pending_payments ?? [];
  const pendingTotal  = pendingItems.reduce((a, p) => a + Number(p.amount ?? 0), 0);
  const balanceDue    = hasReceived
    ? Math.max(0, grossInv - totalRecv - pendingTotal)
    : Math.max(0, Number(deal.post_build_balance_remaining ?? grossInv) - pendingTotal);

  ensureSpace(ctx, 160);

  // Table header row (teal background)
  const tableX = ML;
  const tableW = CONTENT_W;
  const rowH = 28;

  ctx.page.drawRectangle({ x: tableX, y: ctx.y - rowH + 8, width: tableW, height: rowH, color: C.teal });
  ctx.page.drawText("DESCRIPTION", { x: tableX + 12, y: ctx.y - 12, size: 9, font: bold, color: C.white });
  drawRight(ctx, "AMOUNT", { y: ctx.y - 12, size: 9, bold: true, color: C.white, rightX: tableX + tableW - 12 });
  ctx.y -= rowH + 2;

  // Build line items
  type LineItem = { label: string; amount: number; isCredit?: boolean; indent?: boolean };
  const items: LineItem[] = [];

  items.push({ label: "Roofing installation - final invoice", amount: grossInv });
  if (supps > 0) {
    items.push({ label: "Includes approved insurance supplements", amount: supps, indent: true });
  }
  // Oct 29, 2026 — trades billed to the homeowner from the trade sheet: the
  // gross already carries them (calcScope / fn_deal_invoice_gross add
  // trade_ho_total); one indented line each says so.
  for (const t of invoiceTradeLines(deal)) {
    items.push({ label: "Includes " + t.label, amount: t.amount, indent: true });
  }
  if (preRecv > 0) {
    items.push({ label: "Pre-build payment received", amount: -preRecv, isCredit: true });
  }
  if (postRecv > 0) {
    items.push({ label: "Post-build payment received", amount: -postRecv, isCredit: true });
  }
  if (pendingItems.length > 0) {
    for (const p of pendingItems) {
      items.push({ label: p.label, amount: -Number(p.amount ?? 0), isCredit: true });
    }
  } else if (pendingTotal > 0) {
    items.push({ label: "Payment pending - clearing", amount: -pendingTotal, isCredit: true });
  }

  // Draw each row
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    ensureSpace(ctx, 28);

    // Alternating background
    if (i % 2 === 1) {
      ctx.page.drawRectangle({ x: tableX, y: ctx.y - 18, width: tableW, height: 24, color: rgb(0.975, 0.98, 0.985) });
    }

    const labelX = item.indent ? tableX + 24 : tableX + 12;
    const labelColor = item.indent ? C.slate40 : C.slate;
    const labelSz = item.indent ? 9 : 10;

    ctx.page.drawText(asciiOnly(item.label), {
      x: labelX, y: ctx.y - 10, size: labelSz, font: reg, color: labelColor,
    });

    const amtStr = item.isCredit ? "(" + money(Math.abs(item.amount)) + ")" : money(item.amount);
    const amtColor = item.isCredit ? C.red : C.slate;
    const amtW = reg.widthOfTextAtSize(asciiOnly(amtStr), 10);
    ctx.page.drawText(asciiOnly(amtStr), {
      x: tableX + tableW - 12 - amtW, y: ctx.y - 10, size: 10, font: reg, color: amtColor,
    });

    // Bottom border
    ctx.page.drawLine({
      start: { x: tableX, y: ctx.y - 18 },
      end:   { x: tableX + tableW, y: ctx.y - 18 },
      thickness: 0.5, color: C.line,
    });

    ctx.y -= 24;
  }
  ctx.y -= 8;

  // === SUMMARY BOX (dark slate background) ===
  ensureSpace(ctx, 80);
  const sumBoxH = 68 + (pendingTotal > 0 ? 16 : 0);
  const sumBoxW = 280;
  const sumBoxX = PAGE_W - MR - sumBoxW;
  const sumBoxY = ctx.y - sumBoxH + 8;

  ctx.page.drawRectangle({ x: sumBoxX, y: sumBoxY, width: sumBoxW, height: sumBoxH, color: C.slate });

  // Total Invoiced row
  let sY = ctx.y - 8;
  ctx.page.drawText("Total Invoiced", { x: sumBoxX + 16, y: sY, size: 9, font: reg, color: C.slate40 });
  const tiStr = money(grossInv);
  const tiW = reg.widthOfTextAtSize(asciiOnly(tiStr), 10);
  ctx.page.drawText(asciiOnly(tiStr), { x: sumBoxX + sumBoxW - 16 - tiW, y: sY, size: 10, font: reg, color: C.white });

  // Payments Received row. (Label was hardcoded "Pre-build Received" while the
  // figure summed pre + post — wrong on any deal paid post-build. Fixed Aug 14, 2026.)
  if (totalRecv > 0) {
    sY -= 16;
    ctx.page.drawText("Payments Received", { x: sumBoxX + 16, y: sY, size: 9, font: reg, color: C.slate40 });
    const prStr = "-" + money(totalRecv);
    const prW = reg.widthOfTextAtSize(asciiOnly(prStr), 10);
    ctx.page.drawText(asciiOnly(prStr), { x: sumBoxX + sumBoxW - 16 - prW, y: sY, size: 10, font: reg, color: C.slate40 });
  }

  // Pending Payments row
  if (pendingTotal > 0) {
    sY -= 16;
    ctx.page.drawText("Pending Payments", { x: sumBoxX + 16, y: sY, size: 9, font: reg, color: C.slate40 });
    const ppStr = "-" + money(pendingTotal);
    const ppW = reg.widthOfTextAtSize(asciiOnly(ppStr), 10);
    ctx.page.drawText(asciiOnly(ppStr), { x: sumBoxX + sumBoxW - 16 - ppW, y: sY, size: 10, font: reg, color: C.slate40 });
  }

  // Divider line inside box
  sY -= 10;
  ctx.page.drawLine({
    start: { x: sumBoxX + 16, y: sY },
    end:   { x: sumBoxX + sumBoxW - 16, y: sY },
    thickness: 0.5, color: C.slate60,
  });

  // BALANCE DUE in peach
  sY -= 16;
  ctx.page.drawText("BALANCE DUE", { x: sumBoxX + 16, y: sY, size: 10, font: bold, color: C.white });
  const bdueStr = money(balanceDue);
  const bdueW = bold.widthOfTextAtSize(asciiOnly(bdueStr), 16);
  ctx.page.drawText(asciiOnly(bdueStr), { x: sumBoxX + sumBoxW - 16 - bdueW, y: sY - 2, size: 16, font: bold, color: C.peach });

  ctx.y = sumBoxY - 24;

  // === PAY ONLINE section (teal box) ===
  ensureSpace(ctx, 70);
  const payBoxH = 56;
  ctx.page.drawRectangle({ x: ML, y: ctx.y - payBoxH + 8, width: CONTENT_W, height: payBoxH, color: C.teal });

  ctx.page.drawText("Pay securely online", {
    x: ML + 16, y: ctx.y - 10, size: 13, font: bold, color: C.white,
  });
  ctx.page.drawText(asciiOnly("Open the link in your invoice email or text to pay by card or bank transfer (ACH)."), {
    x: ML + 16, y: ctx.y - 26, size: 9.5, font: reg, color: C.sky,
  });
  ctx.page.drawText(asciiOnly("Payments processed securely via Stripe. Most homeowners pay in under 60 seconds."), {
    x: ML + 16, y: ctx.y - 40, size: 8.5, font: reg, color: C.sky,
  });

  ctx.y -= payBoxH + 8;

  // "As of" note — this PDF is a point-in-time snapshot; the live payment
  // link always reflects the current balance, so an older printed copy can
  // never be mistaken for the authoritative amount.
  ensureSpace(ctx, pendingTotal > 0 ? 50 : 24);
  if (pendingTotal > 0) {
    ctx.page.drawText(asciiOnly("Pending payments are bank transfers (ACH) you already submitted; they typically post within 1-4 business days and are"), {
      x: ML, y: ctx.y, size: 8, font: reg, color: C.slate40,
    });
    ctx.y -= 10;
    ctx.page.drawText(asciiOnly("credited above so your balance reflects what is still owed. If a transfer is returned unpaid, that amount goes back on your balance."), {
      x: ML, y: ctx.y, size: 8, font: reg, color: C.slate40,
    });
    ctx.y -= 13;
  }
  ctx.page.drawText(asciiOnly("Balance shown is as of " + today + ". For your current balance, use your secure payment link above."), {
    x: ML, y: ctx.y, size: 8.5, font: reg, color: C.slate40,
  });
  ctx.y -= 20;

  // === PREFER TO PAY BY CHECK? ===
  ensureSpace(ctx, 76);
  ctx.page.drawText("Prefer to pay by check?", { x: ML, y: ctx.y, size: 11, font: bold, color: C.slate });
  ctx.y -= 18;
  ctx.page.drawText(asciiOnly("Make checks payable to:  Peach Duck Contracting Co."), { x: ML, y: ctx.y, size: 10, font: reg, color: C.slate60 });
  ctx.y -= 14;
  ctx.page.drawText(asciiOnly("Memo line:  Invoice " + invNum), { x: ML, y: ctx.y, size: 10, font: reg, color: C.slate60 });
  ctx.y -= 14;
  ctx.page.drawText(asciiOnly("Mail to:  225 Reformation Pkwy, Suite 200, Canton, GA 30114"), { x: ML, y: ctx.y, size: 10, font: reg, color: C.slate60 });
  ctx.y -= 16;

  // Questions line
  ensureSpace(ctx, 16);
  ctx.page.drawText(asciiOnly("Questions? Reply to your invoice email or text (470) 823-8816."), {
    x: ML, y: ctx.y, size: 9, font: reg, color: C.slate40,
  });

  // === FOOTER on every page ===
  const pages = pdf.getPages();
  for (let i = 0; i < pages.length; i++) {
    const tmp: DrawCtx = { ...ctx, page: pages[i] };
    drawPageFooter(tmp);
    // Page number (above footer bar)
    const pnText = "Page " + (i + 1) + " of " + pages.length;
    const pnW = reg.widthOfTextAtSize(pnText, 7);
    pages[i].drawText(pnText, {
      x: PAGE_W - MR - pnW, y: 38, size: 7, font: reg, color: C.slate40,
    });
  }

  return await pdf.save();
}

// --- pending (unsettled) homeowner payments ---------------------------------
//
// Aug 14, 2026. Only Stripe payments still in state 'made' count: those are
// ACH transfers the homeowner has already authorised through the portal that
// have not settled yet. Checks in 'iink_submitted' are carrier funds in
// endorsement, not homeowner payments — excluded on purpose.
//
//   const { data } = await supabase.from("deal_payments")
//     .select("amount, method, state, stripe_made_at")
//     .eq("deal_id", deal.id).eq("method", "stripe").eq("state", "made");
//   const pending = buildPendingPayments(data ?? []);
//   const bytes = await generateSupplementInvoicePDF({ ...deal, pending_payments: pending }, invNum);

// Oct 20, 2026: a payment still waiting on the homeowner's bank verification
// (ach_verify_status 'pending', Stripe ACH typed in by hand) says so.
export function buildPendingPayments(
  rows: {
    amount: number | string | null;
    method?: string | null;
    state?: string | null;
    stripe_made_at?: string | null;
    ach_verify_status?: string | null;
  }[],
): { label: string; amount: number }[] {
  return rows
    .filter((r) => (r.method ?? "stripe") === "stripe" && (r.state ?? "made") === "made" && Number(r.amount ?? 0) > 0)
    .sort((a, b) => String(a.stripe_made_at ?? "").localeCompare(String(b.stripe_made_at ?? "")))
    .map((r) => {
      const when = r.stripe_made_at
        ? new Date(r.stripe_made_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
        : null;
      return {
        label: r.ach_verify_status === "pending"
          ? "Payment pending - waiting on bank account verification"
          : when
          ? "Payment pending - bank transfer submitted " + when
          : "Payment pending - bank transfer clearing",
        amount: Number(r.amount ?? 0),
      };
    });
}

// --- upload helper ----------------------------------------------------------

export async function uploadInvoicePDF(
  supabase: SupabaseClient,
  dealId: string,
  pdfBytes: Uint8Array,
): Promise<string> {
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  const path = "supplement-invoices/" + dealId + "/" + ts + ".pdf";

  const { error } = await supabase.storage
    .from("deal-documents")
    .upload(path, pdfBytes, {
      contentType: "application/pdf",
      upsert: false,
    });
  if (error) throw new Error("upload failed: " + error.message);

  return path;
}

// --- one-shot helper: generate + upload + update deals row + deal_documents --

export async function generateAndAttachInvoicePDF(
  supabase: SupabaseClient,
  deal: InvoiceDeal,
): Promise<{ path: string; bytes: Uint8Array; invoiceNumber: string; signedUrl: string | null }> {
  // Lock the invoice number to this single generation moment, so the PDF
  // content, the deal row, the delivery snapshot, and the public /i/{token}
  // page all show the same number - even on re-sends and across midnight.
  const generatedAt = new Date();
  const invoiceNumber = computeInvoiceNumber(deal, generatedAt);

  // Aug 14, 2026 — pull unsettled homeowner ACH here rather than at each call
  // site, so every path that generates an invoice (Shannon's SendInvoicePanel,
  // the deal Payments tab, any future caller) credits pending payments without
  // needing to remember to. Best-effort: a failed read degrades to the old
  // settled-only behaviour rather than blocking the invoice.
  let pendingPayments: { label: string; amount: number }[] = deal.pending_payments ?? [];
  if (!deal.pending_payments) {
    try {
      const { data: pendRows } = await supabase
        .from("deal_payments")
        .select("amount, method, state, stripe_made_at, ach_verify_status")
        .eq("deal_id", deal.id)
        .eq("method", "stripe")
        .eq("state", "made");
      pendingPayments = buildPendingPayments(pendRows ?? []);
    } catch (e) {
      console.warn("pending payment lookup failed; invoice will show settled payments only:", e);
    }
  }

  const bytes = await generateSupplementInvoicePDF({ ...deal, pending_payments: pendingPayments }, invoiceNumber);
  const path = await uploadInvoicePDF(supabase, deal.id, bytes);

  // Snapshot the figures this PDF was rendered from (same values the PDF used
  // for Total Invoiced + Balance Due). SendInvoicePanel compares these against
  // the live deal so a PDF generated before a scope/payment change can't be
  // sent without a heads-up + regenerate. Jul 14, 2026: the PDF prints GROSS,
  // so the snapshot is gross — keep the stale-compare in SendInvoicePanel on
  // the same figure.
  const snapshotAmount   = Number(deal.initial_invoice_amount ?? deal.final_invoice_amount ?? 0) ||
                           Number(deal.final_invoice_amount ?? 0);
  const snapshotReceived = Number(deal.pre_build_amount_received ?? 0) + Number(deal.post_build_amount_received ?? 0);
  // Pending is snapshotted separately (not folded into received) so the two
  // drift checks stay independent: an ACH settling moves money from pending to
  // received, and SendInvoicePanel should flag that as a stale PDF even though
  // the combined total is unchanged.
  const snapshotPending  = pendingPayments.reduce((a, p) => a + Number(p.amount ?? 0), 0);

  // Update deals row with storage path + metadata
  const { error } = await supabase
    .from("deals")
    .update({
      supplement_invoice_pdf_url: path,
      supplement_invoice_generated_at: generatedAt.toISOString(),
      supplement_invoice_number: invoiceNumber,
      supplement_invoice_pdf_amount: snapshotAmount,
      supplement_invoice_pdf_received: snapshotReceived,
      supplement_invoice_pdf_pending: snapshotPending,
    })
    .eq("id", deal.id);
  if (error) throw new Error("deal update failed: " + error.message);

  // Supersede any existing invoice document for this deal, then insert new one
  // so the PDF appears in the Documents tab and only the latest version shows.
  try {
    const { data: existing } = await supabase
      .from("deal_documents")
      .select("id")
      .eq("deal_id", deal.id)
      .eq("file_type", "invoice")
      .is("superseded_at", null);

    const userId = (await supabase.auth.getUser())?.data?.user?.id ?? null;
    const fileName = "Invoice-" + invoiceNumber + ".pdf";

    // Insert the new document record
    const { data: newDoc, error: insertErr } = await supabase
      .from("deal_documents")
      .insert({
        deal_id: deal.id,
        file_name: fileName,
        file_type: "invoice",
        storage_path: path,
        file_size: bytes.length,
        mime_type: "application/pdf",
        uploaded_by: userId,
        notes: "Auto-generated supplement invoice",
      })
      .select("id")
      .single();

    if (insertErr) {
      console.warn("deal_documents insert warning:", insertErr.message);
    } else if (newDoc && existing && existing.length > 0) {
      // Mark previous versions as superseded
      const oldIds = existing.map((d: { id: string }) => d.id);
      await supabase
        .from("deal_documents")
        .update({ superseded_at: generatedAt.toISOString(), superseded_by: newDoc.id })
        .in("id", oldIds);
    }
  } catch (docErr) {
    // Non-fatal: the PDF is uploaded and deals row is updated;
    // the document just won't appear in the Documents tab until next regeneration.
    console.warn("deal_documents bookkeeping failed:", docErr);
  }

  // Create a signed URL for immediate viewing
  let signedUrl: string | null = null;
  try {
    const { data } = await supabase.storage
      .from("deal-documents")
      .createSignedUrl(path, 3600);
    signedUrl = data?.signedUrl ?? null;
  } catch { /* non-fatal */ }

  return { path, bytes, invoiceNumber, signedUrl };
}
